import React from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardList, Users, CalendarCheck, Lock, CheckCircle2, ArrowRight,
  ShieldCheck, BadgeCheck, Wallet, Stethoscope, Sparkles,
} from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import { organizationSchema, breadcrumbSchema, faqSchema } from '../lib/seoSchemas';
import { MarketplaceDisclaimer } from '../components/brand/MarketplaceDisclaimer';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';

const FAMILY_STEPS = [
  {
    icon: ClipboardList,
    title: 'Tell us what’s going on',
    detail: 'Complete a free care assessment — the condition, the kind of help needed, schedule and budget. It takes a few minutes and no payment details are required.',
    note: 'Free · no payment info needed',
  },
  {
    icon: Users,
    title: 'Review your matches',
    detail: 'As soon as your assessment is complete, CaraLoom matches you with verified clinicians suited to the care need — each with transparent hourly rates, credentials, specialties and availability you can compare side by side.',
    note: 'Matches generated as soon as your assessment is complete',
  },
  {
    icon: CalendarCheck,
    title: 'Agree on the visit',
    detail: 'Choose the clinician you trust, agree on the scope and rate directly with them — no staffing middleman — and pick a time that works.',
    note: 'You choose the clinician — and keep the same one',
  },
  {
    icon: Lock,
    title: 'Secure your visit',
    detail: 'Your payment is secured up-front through Stripe — but the clinician isn’t paid yet. If plans change before the visit, nothing has been settled.',
    note: 'Funds move only after care is verified',
  },
  {
    icon: CheckCircle2,
    title: 'Care happens — then settlement releases',
    detail: 'Once the visit is documented and signed off in the clinician’s SOAP note, the verified-settlement workflow releases funds directly to the clinician. You leave a review, and you can rebook the same clinician for ongoing care.',
    note: 'Funds settle only after the visit is verified',
  },
];

const CLINICIAN_STEPS = [
  {
    icon: BadgeCheck,
    title: 'Apply & get verified',
    detail: 'Pass the 5-point vetting process — identity, license, background screening, references and continuous monitoring.',
  },
  {
    icon: Stethoscope,
    title: 'Set your rates, accept matches',
    detail: 'You decide your hourly rate, your patients and your schedule. CaraLoom routes matched families to you.',
  },
  {
    icon: Wallet,
    title: 'Get paid on completion',
    detail: 'Funds settle directly to you once the visit is documented in your SOAP note. CaraLoom charges a flat 12% service rate — no tier upgrades, no surprise fees. Includes payment processing, insurance, credentialing, AI matching, scheduling, and support.',
  },
];

const FAQS = [
  {
    q: 'How much does CaraLoom cost?',
    a: 'Clinicians set their own transparent hourly rates, and CaraLoom adds a flat 12% service rate per completed booking. The service rate covers payment processing, insurance, credential verification, AI matching, the verified-settlement workflow, and support. The assessment and matching are free. See the Pricing page for typical hourly-rate ranges by care level.',
  },
  {
    q: 'How are clinicians vetted?',
    a: 'Every clinician passes a 5-point process before their first visit: identity verification with biometric ID match, primary-source license validation, multi-state background screening (including sanctions registries), reference checks, and continuous monitoring for as long as they stay on the platform. Full details are on our Trust & Safety page.',
  },
  {
    q: 'Does insurance cover this?',
    a: 'Most services on CaraLoom are private-pay. Whether any portion is reimbursable depends on your individual insurance plan — check with your provider before booking.',
  },
  {
    q: 'What if I need to cancel?',
    a: 'Cancellation terms are shown at the time of booking, before any payment is made. Because payment is held in the verified-settlement workflow until the visit is complete, no funds are released for care that wasn’t delivered.',
  },
  {
    q: 'Where is CaraLoom available?',
    a: 'CaraLoom is available across the United States in a growing number of cities. The fastest way to check your area is to browse the marketplace or search care near you.',
  },
  {
    q: 'What happens if something goes wrong during a visit?',
    a: 'Report it immediately through the Contact page or care@caraloom.com — safety reports are reviewed by our Trust & Safety team and prioritized ahead of every other request. Your payment is held in the verified-settlement workflow until the visit is properly completed.',
  },
];

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen bg-cn-bg" data-testid="how-it-works-page">
      <Seo
        title="How CaraLoom Works | From Care Need to First Visit"
        description="Exactly what happens after you sign up: free assessment, verified clinician matches with transparent rates, direct booking, payment held until the visit is verified, and funds that release only after care is delivered."
        path="/how-it-works"
        keywords={['how CaraLoom works', 'book home care online', 'home care verified settlement']}
        schema={[
          organizationSchema(),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'How it works', path: '/how-it-works' }]),
          faqSchema(FAQS.map(({ q, a }) => ({ question: q, answer: a }))),
        ]}
      />
      <PublicNavbar />

      {/* Hero */}
      <section className="border-b border-cn-border bg-white dark:bg-cn-ink-900">
        <div className="cn-container py-16 md:py-24 max-w-3xl">
          <div className="cn-eyebrow mb-3">How it works</div>
          <h1 className="cn-display text-4xl md:text-6xl text-cn-text leading-tight">
            From care need to first visit —<br />
            <span className="italic font-medium text-cn-primary">here’s exactly what happens.</span>
          </h1>
          <p className="mt-6 text-cn-text-muted text-lg leading-relaxed">
            CaraLoom is a marketplace that connects you directly with verified, independent
            clinicians — we don’t provide care ourselves. No phone tag, no surprise pricing,
            and no money moves until care is delivered.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/auth" className="cn-btn cn-btn-primary text-base" data-testid="hiw-start-cta">
              Start the free assessment <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/patient/find-clinicians" className="cn-btn cn-btn-ghost text-base" data-testid="hiw-browse-cta">
              Browse clinicians first
            </Link>
          </div>
        </div>
      </section>

      {/* Family timeline */}
      <section className="py-20 md:py-24" data-testid="hiw-family-timeline">
        <div className="cn-container">
          <div className="max-w-2xl mb-12">
            <div className="cn-eyebrow mb-3">For families</div>
            <h2 className="cn-display text-4xl">What happens after you sign up.</h2>
          </div>
          <ol className="space-y-4">
            {FAMILY_STEPS.map((s, i) => (
              <li
                key={s.title}
                className="cn-card cn-card-hover p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-5"
                data-testid={`hiw-step-${i + 1}`}
              >
                <div className="flex items-center gap-4 md:w-80 flex-shrink-0">
                  <div className="font-mono text-sm text-cn-text-muted w-6 flex-shrink-0">0{i + 1}</div>
                  <div className="w-12 h-12 rounded-2xl bg-cn-sage-100 flex items-center justify-center flex-shrink-0">
                    <s.icon className="w-5 h-5 text-cn-primary" />
                  </div>
                  <h3 className="font-display font-semibold text-lg text-cn-text leading-tight">{s.title}</h3>
                </div>
                <div className="md:flex-1 md:border-l md:border-cn-border md:pl-6">
                  <p className="text-sm text-cn-text-muted leading-relaxed">{s.detail}</p>
                  <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-cn-primary">
                    <Sparkles className="w-3.5 h-3.5" /> {s.note}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Verified-settlement explainer */}
      <section className="py-20 md:py-24 bg-cn-bone-100 dark:bg-cn-ink-900 border-y border-cn-border" data-testid="hiw-verified-settlement">
        <div className="cn-container grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="cn-eyebrow mb-3">Clinical-quality guarantee</div>
            <h2 className="cn-display text-4xl">Your money waits until the care is verified.</h2>
            <p className="mt-4 text-cn-text-muted leading-relaxed">
              When you book, your payment is secured up-front through Stripe — but it is not
              transferred to the clinician until the visit is documented and signed in their
              SOAP note. That protects both sides: families never pay for care that wasn’t
              delivered, and clinicians know payment is already secured before they walk in.
            </p>
            <p className="mt-3 text-xs text-cn-text-muted">
              CaraLoom is a software platform — not a bank, money transmitter, or escrow agent.
              Stripe Connect facilitates the direct clinician-to-family transaction once the
              service-verification milestone is met.
            </p>
          </div>
          <div className="cn-card p-7">
            <div className="space-y-4">
              {[
                { icon: Lock, label: 'You book a visit', sub: 'Payment is secured via Stripe' },
                { icon: ShieldCheck, label: 'Care is delivered', sub: 'The visit is completed and confirmed' },
                { icon: Wallet, label: 'Settlement releases', sub: 'The clinician is paid — you leave a review' },
              ].map((row, i) => (
                <div key={row.label} className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-cn-soft flex items-center justify-center flex-shrink-0">
                    <row.icon className="w-4 h-4 text-cn-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-cn-text">{i + 1}. {row.label}</p>
                    <p className="text-xs text-cn-text-muted">{row.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Clinician strip */}
      <section className="py-20 md:py-24" data-testid="hiw-clinician-strip">
        <div className="cn-container">
          <div className="max-w-2xl mb-12">
            <div className="cn-eyebrow mb-3">For clinicians</div>
            <h2 className="cn-display text-4xl">Three steps to your own practice.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {CLINICIAN_STEPS.map((s, i) => (
              <div key={s.title} className="cn-card p-6" data-testid={`hiw-clin-step-${i + 1}`}>
                <div className="w-10 h-10 rounded-xl bg-cn-sage-100 flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5 text-cn-primary" />
                </div>
                <h3 className="font-display font-semibold">{i + 1}. {s.title}</h3>
                <p className="text-sm text-cn-text-muted mt-1 leading-relaxed">{s.detail}</p>
              </div>
            ))}
          </div>
          <Link to="/pricing" className="inline-flex items-center gap-1.5 mt-8 text-sm font-semibold text-cn-primary hover:underline" data-testid="hiw-clinicians-link">
            Learn more about practicing on CaraLoom <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24 bg-cn-bone-100 dark:bg-cn-ink-900 border-y border-cn-border" data-testid="hiw-faq">
        <div className="cn-container max-w-3xl">
          <div className="mb-10">
            <div className="cn-eyebrow mb-3">Questions families ask</div>
            <h2 className="cn-display text-4xl">Before you book.</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="cn-card px-6 border-cn-border" data-testid={`hiw-faq-${i + 1}`}>
                <AccordionTrigger className="text-left font-display font-semibold text-base hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-cn-text-muted leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-24">
        <div className="cn-container">
          <div className="cn-card p-10 md:p-14 !bg-cn-primary text-white text-center">
            <h2 className="cn-display text-3xl md:text-4xl !text-white">Pay nothing until care is delivered.</h2>
            <p className="mt-4 text-white/80 max-w-xl mx-auto">
              Complete the free assessment and compare verified clinicians at transparent rates —
              your payment is held until the visit is verified.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/auth" className="cn-btn cn-btn-accent text-base" data-testid="hiw-final-cta">
                Start the free assessment <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/pricing" className="cn-btn text-base border border-white/30 text-white hover:bg-white/10" data-testid="hiw-final-clin-cta">
                I’m a clinician
              </Link>
            </div>
          </div>
          <div className="mt-10">
            <MarketplaceDisclaimer variant="banner" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
